import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { TEAM_ROLES } from '../data/nurseAppModels';
import { notificationModel } from '../data/notificationModel';
import { DEFAULT_CATALOGS } from '../data/defaultCatalogs';
import { DEFAULT_LOCATIONS, DEFAULT_DEPARTMENTS, DEFAULT_WARDS, DEFAULT_BEDS } from '../data/defaultTeamConfigs';

const TeamContext = createContext();

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within TeamProvider');
  }
  return context;
};

export const TeamProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [teams, setTeams] = useState([]);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch user's teams with real-time updates
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      setTeams([]);
      setCurrentTeam(null);
      return;
    }

    const userRef = doc(db, 'users', currentUser.uid);
    
    // Use onSnapshot for real-time updates
    const unsubscribe = onSnapshot(
      userRef,
      async (userSnap) => {
        try {
          setLoading(true);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const userTeams = userData.teams || [];

            if (userTeams.length > 0) {
              // Remove duplicate teamIds (keep first occurrence)
              const uniqueTeamRefs = [];
              const seenTeamIds = new Set();
              userTeams.forEach(teamRef => {
                if (teamRef.teamId && !seenTeamIds.has(teamRef.teamId)) {
                  seenTeamIds.add(teamRef.teamId);
                  uniqueTeamRefs.push(teamRef);
                }
              });

              const teamPromises = uniqueTeamRefs.map(async (teamRef) => {
                const teamDoc = await getDoc(doc(db, 'teams', teamRef.teamId));
                if (teamDoc.exists()) {
                  return {
                    id: teamDoc.id,
                    ...teamDoc.data(),
                    userRole: teamRef.teamRole,
                    joinDate: teamRef.joinDate
                  };
                }
                return null;
              });

              const teamsData = (await Promise.all(teamPromises)).filter(Boolean);
              
              // Additional deduplication by team id (in case of any edge cases)
              const uniqueTeams = [];
              const seenTeamIds2 = new Set();
              teamsData.forEach(team => {
                if (team.id && !seenTeamIds2.has(team.id)) {
                  seenTeamIds2.add(team.id);
                  uniqueTeams.push(team);
                }
              });
              
              setTeams(uniqueTeams);

              // Set current team from localStorage or first team
              if (uniqueTeams.length > 0) {
                const savedTeamId = localStorage.getItem('nurseapp_currentTeamId');
                const savedTeam = savedTeamId 
                  ? uniqueTeams.find(t => t.id === savedTeamId)
                  : null;
                
                if (savedTeam) {
                  setCurrentTeam(savedTeam);
                } else if (!currentTeam) {
                  setCurrentTeam(uniqueTeams[0]);
                  localStorage.setItem('nurseapp_currentTeamId', uniqueTeams[0].id);
                }
              }
            } else {
              // No teams, clear state
              setTeams([]);
              setCurrentTeam(null);
            }
          } else {
            // User document doesn't exist, clear state
            setTeams([]);
            setCurrentTeam(null);
          }
        } catch (err) {
          console.error('Error fetching teams:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error in user snapshot:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, refreshTrigger]);

  // Create Team
  const createTeam = async (teamData) => {
    try {
      if (!currentUser) {
        throw new Error('กรุณาเข้าสู่ระบบ');
      }

      const newTeam = {
        name: teamData.name,
        description: teamData.description || '',
        type: teamData.type || 'DEPARTMENT',
        leaderId: currentUser.uid,
        leaderName: currentUser.displayName || currentUser.email,
        deputyLeaders: [],
        members: [],
        settings: {
          maxMembers: teamData.maxMembers || 50,
          allowMemberInvite: teamData.allowMemberInvite !== false
        },
        status: 'ACTIVE',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const teamRef = await addDoc(collection(db, 'teams'), newTeam);
      
      // Add leader to members array
      await updateDoc(teamRef, {
        members: arrayUnion({
          uid: currentUser.uid,
          name: currentUser.displayName || currentUser.email,
          role: currentUser.role || 'NURSE',
          teamRole: TEAM_ROLES.LEADER,
          joinDate: Timestamp.now(),
          status: 'ACTIVE'
        })
      });

      // Update user's teams array
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        teams: arrayUnion({
          teamId: teamRef.id,
          teamName: newTeam.name,
          teamRole: TEAM_ROLES.LEADER,
          joinDate: Timestamp.now(),
          status: 'ACTIVE'
        })
      });

      // Create default catalogs for the new team
      try {
        await createDefaultCatalogs(teamRef.id, currentUser.uid);
      } catch (catalogError) {
        console.error('Error creating default catalogs:', catalogError);
        // Don't fail team creation if catalog creation fails
      }

      // Create default team configs (locations, departments, wards)
      try {
        await createDefaultTeamConfigs(teamRef.id, currentUser.uid);
      } catch (configError) {
        console.error('Error creating default team configs:', configError);
        // Don't fail team creation if config creation fails
      }

      // Create default beds for the new team
      try {
        await createDefaultBeds(teamRef.id, currentUser.uid);
      } catch (bedError) {
        console.error('Error creating default beds:', bedError);
        // Don't fail team creation if bed creation fails
      }

      const createdTeam = {
        id: teamRef.id,
        ...newTeam,
        userRole: TEAM_ROLES.LEADER
      };

      setTeams([...teams, createdTeam]);
      setCurrentTeam(createdTeam);

      return { success: true, team: createdTeam };
    } catch (err) {
      console.error('Error creating team:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Join Team
  const joinTeam = async (teamId, requestMessage = '') => {
    try {
      if (!currentUser) {
        throw new Error('กรุณาเข้าสู่ระบบ');
      }

      const teamRef = doc(db, 'teams', teamId);
      const teamSnap = await getDoc(teamRef);

      if (!teamSnap.exists()) {
        throw new Error('ไม่พบทีม');
      }

      const teamData = teamSnap.data();

      // Check if already a member
      const isMember = teamData.members?.some(m => m.uid === currentUser.uid);
      if (isMember) {
        throw new Error('คุณเป็นสมาชิกของทีมนี้แล้ว');
      }

      // Create join request
      const requestRef = await addDoc(collection(db, 'teamRequests'), {
        teamId: teamId,
        teamName: teamData.name,
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        userRole: currentUser.role || 'NURSE',
        message: requestMessage,
        status: 'PENDING',
        requestedAt: serverTimestamp()
      });

      // Send notification to team leaders
      try {
        const leaders = [
          { uid: teamData.leaderId },
          ...(teamData.deputyLeaders || []).map(dl => ({ uid: dl.uid }))
        ];
        
        const notificationsRef = collection(db, notificationModel.collection);
        const notificationPromises = leaders.map(leader => {
          const notification = notificationModel.createNotification(
            leader.uid,
            notificationModel.types.TEAM_REQUEST,
            '👥 มีคำขอเข้าร่วมทีม',
            `${currentUser.displayName || currentUser.email} ขอเข้าร่วมทีม "${teamData.name}"`,
            {
              teamId: teamId,
              requestId: requestRef.id,
              userId: currentUser.uid
            }
          );
          return addDoc(notificationsRef, {
            ...notification,
            read: false,
            readAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        });
        
        await Promise.all(notificationPromises);
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
        // Don't throw error, just log it
      }

      return { success: true, requestId: requestRef.id };
    } catch (err) {
      console.error('Error joining team:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Invite Member
  const inviteMember = async (teamId, userId, userRole = TEAM_ROLES.MEMBER) => {
    try {
      if (!currentUser) {
        throw new Error('กรุณาเข้าสู่ระบบ');
      }

      const teamRef = doc(db, 'teams', teamId);
      const teamSnap = await getDoc(teamRef);

      if (!teamSnap.exists()) {
        throw new Error('ไม่พบทีม');
      }

      const teamData = teamSnap.data();

      // Check permissions (only Leader and Deputy Leaders can invite)
      const userTeamRole = teams.find(t => t.id === teamId)?.userRole;
      if (userTeamRole !== TEAM_ROLES.LEADER && userTeamRole !== TEAM_ROLES.DEPUTY_LEADER) {
        throw new Error('คุณไม่มีสิทธิ์เชิญสมาชิก');
      }

      // Get user data
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        throw new Error('ไม่พบผู้ใช้');
      }

      const userData = userSnap.data();

      // Create invitation
      const invitationRef = await addDoc(collection(db, 'teamRequests'), {
        teamId: teamId,
        teamName: teamData.name,
        userId: userId,
        userName: userData.displayName || userData.email,
        userRole: userData.role || 'NURSE',
        invitedBy: currentUser.uid,
        invitedByName: currentUser.displayName || currentUser.email,
        teamRole: userRole,
        status: 'PENDING',
        type: 'INVITATION',
        requestedAt: serverTimestamp()
      });

      // Send notification to invited user
      try {
        const notificationsRef = collection(db, notificationModel.collection);
        const notification = notificationModel.createNotification(
          userId,
          notificationModel.types.TEAM_INVITATION,
          '📨 คุณได้รับคำเชิญเข้าทีม',
          `${currentUser.displayName || currentUser.email} เชิญคุณเข้าร่วมทีม "${teamData.name}"`,
          {
            teamId: teamId,
            requestId: invitationRef.id,
            invitedBy: currentUser.uid
          }
        );
        await addDoc(notificationsRef, {
          ...notification,
          read: false,
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
        // Don't throw error, just log it
      }

      return { success: true, invitationId: invitationRef.id };
    } catch (err) {
      console.error('Error inviting member:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Approve/Reject Request
  const handleRequest = async (requestId, action, teamRole = TEAM_ROLES.MEMBER) => {
    try {
      if (!currentUser) {
        throw new Error('กรุณาเข้าสู่ระบบ');
      }

      const requestRef = doc(db, 'teamRequests', requestId);
      const requestSnap = await getDoc(requestRef);

      if (!requestSnap.exists()) {
        throw new Error('ไม่พบคำขอ');
      }

      const requestData = requestSnap.data();
      const teamRef = doc(db, 'teams', requestData.teamId);

      if (action === 'APPROVE') {
        // Check if request is already approved
        if (requestData.status === 'APPROVED') {
          throw new Error('คำขอนี้ได้รับการอนุมัติแล้ว');
        }

        // Check if user is already a member
        const teamSnap = await getDoc(teamRef);
        if (!teamSnap.exists()) {
          throw new Error('ไม่พบทีม');
        }
        
        const teamData = teamSnap.data();
        const existingMember = teamData.members?.find(m => m.uid === requestData.userId);
        if (existingMember) {
          // Member already exists, just update the request status
          await updateDoc(requestRef, {
            status: 'APPROVED',
            approvedBy: currentUser.uid,
            approvedAt: serverTimestamp(),
            teamRole: teamRole
          });
          return { success: true, message: 'สมาชิกอยู่ในทีมแล้ว' };
        }

        // Add member to team
        const memberData = {
          uid: requestData.userId,
          name: requestData.userName,
          role: requestData.userRole,
          teamRole: teamRole,
          joinDate: Timestamp.now(),
          status: 'ACTIVE'
        };

        await updateDoc(teamRef, {
          members: arrayUnion(memberData),
          updatedAt: serverTimestamp()
        });

        // If Deputy Leader, add to deputyLeaders array
        if (teamRole === TEAM_ROLES.DEPUTY_LEADER) {
          await updateDoc(teamRef, {
            deputyLeaders: arrayUnion({
              uid: requestData.userId,
              name: requestData.userName,
              assignedAt: Timestamp.now()
            })
          });
        }

        // Update user's teams array
        const userRef = doc(db, 'users', requestData.userId);
        await updateDoc(userRef, {
          teams: arrayUnion({
            teamId: requestData.teamId,
            teamName: requestData.teamName,
            teamRole: teamRole,
            joinDate: Timestamp.now(),
            status: 'ACTIVE'
          })
        });

        // Update request status
        await updateDoc(requestRef, {
          status: 'APPROVED',
          approvedBy: currentUser.uid,
          approvedAt: serverTimestamp(),
          teamRole: teamRole
        });

        // Send notification to approved user
        try {
          const notificationsRef = collection(db, notificationModel.collection);
          const notification = notificationModel.createNotification(
            requestData.userId,
            notificationModel.types.TEAM_APPROVED,
            '✅ คำขอเข้าร่วมทีมถูกอนุมัติ',
            `คำขอเข้าร่วมทีม "${requestData.teamName}" ถูกอนุมัติแล้ว`,
            {
              teamId: requestData.teamId,
              requestId: requestId
            }
          );
          await addDoc(notificationsRef, {
            ...notification,
            read: false,
            readAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (notifError) {
          console.error('Error sending notification:', notifError);
        }
      } else {
        // Reject request
        await updateDoc(requestRef, {
          status: 'REJECTED',
          rejectedBy: currentUser.uid,
          rejectedAt: serverTimestamp()
        });

        // Send notification to rejected user
        try {
          const notificationsRef = collection(db, notificationModel.collection);
          const notification = notificationModel.createNotification(
            requestData.userId,
            notificationModel.types.TEAM_REJECTED,
            '❌ คำขอเข้าร่วมทีมถูกปฏิเสธ',
            `คำขอเข้าร่วมทีม "${requestData.teamName}" ถูกปฏิเสธ`,
            {
              teamId: requestData.teamId,
              requestId: requestId
            }
          );
          await addDoc(notificationsRef, {
            ...notification,
            read: false,
            readAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (notifError) {
          console.error('Error sending notification:', notifError);
        }
      }

      // Refresh teams for the user who was approved
      // If the approved user is the current user, refresh their teams list
      if (action === 'APPROVE' && requestData.userId === currentUser.uid) {
        setRefreshTrigger(prev => prev + 1);
      }

      // Also refresh the team data for the team owner
      const teamSnap = await getDoc(teamRef);
      if (teamSnap.exists()) {
        const updatedTeam = {
          id: teamSnap.id,
          ...teamSnap.data(),
          userRole: teams.find(t => t.id === teamSnap.id)?.userRole
        };
        setTeams(teams.map(t => t.id === updatedTeam.id ? updatedTeam : t));
      }

      return { success: true };
    } catch (err) {
      console.error('Error handling request:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Remove Member
  const removeMember = async (teamId, userId) => {
    try {
      if (!currentUser) {
        throw new Error('กรุณาเข้าสู่ระบบ');
      }

      const teamRef = doc(db, 'teams', teamId);
      const teamSnap = await getDoc(teamRef);

      if (!teamSnap.exists()) {
        throw new Error('ไม่พบทีม');
      }

      const teamData = teamSnap.data();
      const userTeamRole = teams.find(t => t.id === teamId)?.userRole;

      // Only Leader can remove members
      if (userTeamRole !== TEAM_ROLES.LEADER) {
        throw new Error('คุณไม่มีสิทธิ์ลบสมาชิก');
      }

      // Find member to remove
      const member = teamData.members?.find(m => m.uid === userId);
      if (!member) {
        throw new Error('ไม่พบสมาชิก');
      }

      // Remove from members array
      await updateDoc(teamRef, {
        members: arrayRemove(member),
        updatedAt: serverTimestamp()
      });

      // Remove from deputyLeaders if applicable
      if (member.teamRole === TEAM_ROLES.DEPUTY_LEADER) {
        const deputy = teamData.deputyLeaders?.find(d => d.uid === userId);
        if (deputy) {
          await updateDoc(teamRef, {
            deputyLeaders: arrayRemove(deputy)
          });
        }
      }

      // Remove from user's teams array
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userTeams = userSnap.data().teams || [];
        const updatedTeams = userTeams.filter(t => t.teamId !== teamId);
        await updateDoc(userRef, { teams: updatedTeams });
      }

      // Send notification to removed member
      try {
        const notificationsRef = collection(db, notificationModel.collection);
        const notification = notificationModel.createNotification(
          userId,
          notificationModel.types.TEAM_MEMBER_REMOVED,
          '🚫 คุณถูกลบออกจากทีม',
          `คุณถูกลบออกจากทีม "${teamData.name}"`,
          {
            teamId: teamId
          }
        );
        await addDoc(notificationsRef, {
          ...notification,
          read: false,
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
      }

      // Refresh teams
      const updatedTeamSnap = await getDoc(teamRef);
      if (updatedTeamSnap.exists()) {
        const updatedTeam = {
          id: updatedTeamSnap.id,
          ...updatedTeamSnap.data(),
          userRole: userTeamRole
        };
        setTeams(teams.map(t => t.id === updatedTeam.id ? updatedTeam : t));
      }

      return { success: true };
    } catch (err) {
      console.error('Error removing member:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Cancel Request
  const cancelRequest = async (requestId) => {
    try {
      if (!currentUser) {
        throw new Error('กรุณาเข้าสู่ระบบ');
      }

      const requestRef = doc(db, 'teamRequests', requestId);
      const requestSnap = await getDoc(requestRef);

      if (!requestSnap.exists()) {
        throw new Error('ไม่พบคำขอ');
      }

      const requestData = requestSnap.data();

      // Only the requester can cancel their own request
      if (requestData.userId !== currentUser.uid) {
        throw new Error('คุณไม่มีสิทธิ์ยกเลิกคำขอนี้');
      }

      // Only pending requests can be cancelled
      if (requestData.status !== 'PENDING') {
        throw new Error('ไม่สามารถยกเลิกคำขอที่ได้รับการอนุมัติหรือปฏิเสธแล้ว');
      }

      // Update request status
      await updateDoc(requestRef, {
        status: 'CANCELLED',
        cancelledAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error cancelling request:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Change Member Role (Leader only)
  const changeMemberRole = async (teamId, userId, newRole) => {
    try {
      if (!currentUser) {
        throw new Error('กรุณาเข้าสู่ระบบ');
      }

      const teamRef = doc(db, 'teams', teamId);
      const teamSnap = await getDoc(teamRef);

      if (!teamSnap.exists()) {
        throw new Error('ไม่พบทีม');
      }

      const teamData = teamSnap.data();
      const userTeamRole = teams.find(t => t.id === teamId)?.userRole;

      // Only Leader can change member roles
      if (userTeamRole !== TEAM_ROLES.LEADER) {
        throw new Error('คุณไม่มีสิทธิ์เปลี่ยนสิทธิ์สมาชิก');
      }

      // Cannot change Leader's role
      const member = teamData.members?.find(m => m.uid === userId);
      if (!member) {
        throw new Error('ไม่พบสมาชิก');
      }

      if (member.teamRole === TEAM_ROLES.LEADER) {
        throw new Error('ไม่สามารถเปลี่ยนสิทธิ์ของหัวหน้าทีมได้');
      }

      // Validate new role
      if (![TEAM_ROLES.MEMBER, TEAM_ROLES.DEPUTY_LEADER].includes(newRole)) {
        throw new Error('สิทธิ์ไม่ถูกต้อง');
      }

      // Check Deputy Leader limit (max 2)
      if (newRole === TEAM_ROLES.DEPUTY_LEADER) {
        const currentDeputyLeaders = teamData.deputyLeaders || [];
        const existingDeputyCount = currentDeputyLeaders.filter(
          d => d.uid !== userId
        ).length;
        
        if (existingDeputyCount >= 2) {
          throw new Error('รองหัวหน้าทีมมีได้ไม่เกิน 2 คน');
        }
      }

      // Update member's role in members array
      const updatedMembers = teamData.members.map(m => {
        if (m.uid === userId) {
          return {
            ...m,
            teamRole: newRole
          };
        }
        return m;
      });

      await updateDoc(teamRef, {
        members: updatedMembers,
        updatedAt: serverTimestamp()
      });

      // Update deputyLeaders array
      const currentDeputyLeaders = teamData.deputyLeaders || [];
      
      if (newRole === TEAM_ROLES.DEPUTY_LEADER) {
        // Add to deputyLeaders if not already there
        const isAlreadyDeputy = currentDeputyLeaders.some(d => d.uid === userId);
        if (!isAlreadyDeputy) {
          await updateDoc(teamRef, {
            deputyLeaders: arrayUnion({
              uid: userId,
              name: member.name,
              assignedAt: Timestamp.now(),
              assignedBy: currentUser.uid
            })
          });
        }
      } else if (member.teamRole === TEAM_ROLES.DEPUTY_LEADER && newRole === TEAM_ROLES.MEMBER) {
        // Remove from deputyLeaders
        const deputy = currentDeputyLeaders.find(d => d.uid === userId);
        if (deputy) {
          await updateDoc(teamRef, {
            deputyLeaders: arrayRemove(deputy)
          });
        }
      }

      // Update user's teams array
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userTeams = userSnap.data().teams || [];
        const updatedTeams = userTeams.map(t => {
          if (t.teamId === teamId) {
            return {
              ...t,
              teamRole: newRole
            };
          }
          return t;
        });
        await updateDoc(userRef, { teams: updatedTeams });
      }

      // Send notification to member
      try {
        const notificationsRef = collection(db, notificationModel.collection);
        const roleLabel = newRole === TEAM_ROLES.DEPUTY_LEADER ? 'รองหัวหน้าทีม' : 'สมาชิก';
        const notification = notificationModel.createNotification(
          userId,
          notificationModel.types.TEAM_ROLE_CHANGED || 'team_role_changed',
          '🔐 สิทธิ์ของคุณถูกเปลี่ยน',
          `คุณได้รับสิทธิ์เป็น "${roleLabel}" ในทีม "${teamData.name}"`,
          {
            teamId: teamId,
            newRole: newRole
          }
        );
        await addDoc(notificationsRef, {
          ...notification,
          read: false,
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
      }

      // Refresh teams
      const updatedTeamSnap = await getDoc(teamRef);
      if (updatedTeamSnap.exists()) {
        const updatedTeam = {
          id: updatedTeamSnap.id,
          ...updatedTeamSnap.data(),
          userRole: userTeamRole
        };
        setTeams(teams.map(t => t.id === updatedTeam.id ? updatedTeam : t));
      }

      return { success: true };
    } catch (err) {
      console.error('Error changing member role:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Set Current Team
  const setTeam = (team) => {
    setCurrentTeam(team);
    // Save to localStorage for persistence
    if (team) {
      localStorage.setItem('nurseapp_currentTeamId', team.id);
    } else {
      localStorage.removeItem('nurseapp_currentTeamId');
    }
  };

  // Helper function to create default catalogs
  const createDefaultCatalogs = async (teamId, createdByUserId) => {
    try {
      const catalogsRef = collection(db, 'equipmentCatalogs');
      
      for (const catalog of DEFAULT_CATALOGS) {
        await addDoc(catalogsRef, {
          ...catalog,
          teamId,
          createdBy: createdByUserId,
          createdByName: currentUser?.displayName || currentUser?.email || 'System',
          status: 'ACTIVE',
          usageCount: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      console.log(`✅ Created ${DEFAULT_CATALOGS.length} default catalogs for team ${teamId}`);
    } catch (err) {
      console.error('Error creating default catalogs:', err);
      throw err;
    }
  };

  // Helper function to create default team configs
  const createDefaultTeamConfigs = async (teamId, createdByUserId) => {
    try {
      const configsRef = collection(db, 'teamConfigs');
      
      // Create locations config
      await addDoc(configsRef, {
        teamId,
        type: 'LOCATIONS',
        values: DEFAULT_LOCATIONS,
        isDefault: true,
        createdBy: createdByUserId,
        createdByName: currentUser?.displayName || currentUser?.email || 'System',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Create departments config
      await addDoc(configsRef, {
        teamId,
        type: 'DEPARTMENTS',
        values: DEFAULT_DEPARTMENTS,
        isDefault: true,
        createdBy: createdByUserId,
        createdByName: currentUser?.displayName || currentUser?.email || 'System',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Create wards config
      await addDoc(configsRef, {
        teamId,
        type: 'WARDS',
        values: DEFAULT_WARDS,
        isDefault: true,
        createdBy: createdByUserId,
        createdByName: currentUser?.displayName || currentUser?.email || 'System',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log(`✅ Created default team configs for team ${teamId}`);
    } catch (err) {
      console.error('Error creating default team configs:', err);
      throw err;
    }
  };

  // Helper function to create default beds
  const createDefaultBeds = async (teamId, createdByUserId) => {
    try {
      const bedsRef = collection(db, 'teamBeds');
      
      // Create default beds
      const bedPromises = DEFAULT_BEDS.map((bed, index) => 
        addDoc(bedsRef, {
          teamId,
          bedNumber: bed.bedNumber,
          bedName: bed.bedName,
          bedLocation: '',
          ward: bed.ward,
          department: bed.department,
          floor: '',
          room: '',
          position: { x: 0, y: 0 },
          status: 'AVAILABLE',
          isDefault: true,
          createdBy: createdByUserId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
      );

      await Promise.all(bedPromises);
      console.log(`✅ Created ${DEFAULT_BEDS.length} default beds for team ${teamId}`);
    } catch (err) {
      console.error('Error creating default beds:', err);
      throw err;
    }
  };

  const value = {
    teams,
    currentTeam,
    loading,
    error,
    createTeam,
    joinTeam,
    inviteMember,
    handleRequest,
    removeMember,
    cancelRequest,
    changeMemberRole,
    setTeam,
    refreshTeams: () => {
      // Trigger refresh by updating refreshTrigger
      setRefreshTrigger(prev => prev + 1);
    }
  };

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  );
};


